import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Arrays } from '@crm/utils';

/**
 * Composant RadioGroup personnalisé avec direction horizontale
 */
const RowRadioGroup = withStyles({
  root: {
    flexDirection: 'row',
  },
  checked: {},
})(RadioGroup);

/**
 * Interface pour les props du composant SpotTimingsFilter
 */
interface Props {
  /** Créneaux horaires du site */
  timings: CRM.SpotTimings;
  /** Occurrence actuellement incluse dans le filtre */
  includingOccurrence?: CRM.SpotTimingsOccurence;
  /** Fonction appelée lors du changement de sélection */
  onChange: (occurrence?: CRM.SpotTimingsOccurence) => any;
}

/**
 * Génère un label formaté pour un créneau horaire
 * 
 * @param occurence Créneau horaire à formater
 * @returns Label formaté (ex: "09h30")
 */
function labelOf(occurence: CRM.Time) {
  return `${String(occurence.hour).padStart(2, '0')}h${String(
    occurence.minute,
  ).padStart(2, '0')}`;
}

/**
 * Composant de filtre par créneaux horaires (sélection unique)
 * 
 * Ce composant permet de :
 * - Filtrer par un créneau horaire spécifique
 * - Afficher tous les créneaux disponibles
 * - Option "Voir tout" pour désactiver le filtre
 * - Sélection par radio buttons horizontaux
 * 
 * Logique métier :
 * - Génération automatique des labels formatés (HHhMM)
 * - Gestion de la sélection unique via RadioGroup
 * - Callback avec l'occurrence sélectionnée ou undefined
 * - Interface utilisateur responsive
 * 
 * @param timings Créneaux horaires du site
 * @param includingOccurrence Occurrence actuellement incluse dans le filtre
 * @param onChange Fonction appelée lors du changement de sélection
 * @returns Composant React de filtre par créneaux
 */
export const SpotTimingsFilter = ({
  timings,
  includingOccurrence,
  onChange,
}: Props) => {
  const every = useMemo(
    () => timings.every.map(every => ({ ...every, label: labelOf(every) })),
    [timings],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedLabel = (event.target as HTMLInputElement).value;

      if (!selectedLabel) {
        return onChange(undefined);
      }

      onChange(every.find(timing => timing.label === selectedLabel));
    },
    [onChange, every],
  );

  return (
    <Box>
      <Typography>
        <b>Créneaux</b>
      </Typography>
      <Box>
        <RowRadioGroup
          value={includingOccurrence ? labelOf(includingOccurrence) : ''}
          onChange={handleChange}
        >
          {every.map(everyOccurrence => (
            <FormControlLabel
              key={everyOccurrence.label}
              label={everyOccurrence.label}
              value={everyOccurrence.label}
              labelPlacement="end"
              control={<Radio />}
            />
          ))}
          <FormControlLabel
            label={'Voir tout'}
            value={''}
            labelPlacement="end"
            control={<Radio />}
          />
        </RowRadioGroup>
      </Box>
    </Box>
  );
};

/**
 * Interface pour les props du composant SpotMultiTimingsFilter
 */
interface MultiProps {
  /** Créneaux horaires du site */
  timings: CRM.SpotTimings;
  /** Occurrences actuellement incluses dans le filtre */
  includingOccurrences: CRM.SpotTimingsOccurence[];
  /** Fonction appelée lors du changement de sélection */
  onChange: (occurrences: CRM.SpotTimingsOccurence[]) => any;
}

/**
 * Composant de filtre par créneaux horaires (sélection multiple)
 * 
 * Ce composant permet de :
 * - Filtrer par plusieurs créneaux horaires simultanément
 * - Cocher/décocher des créneaux avec des checkboxes
 * - Gestion de la sélection multiple
 * - Ajout/suppression dynamique des créneaux
 * 
 * Logique métier :
 * - Toggle des créneaux sélectionnés
 * - Comparaison par heure et minute pour éviter les doublons
 * - Utilisation des utilitaires Arrays pour la manipulation
 * - Interface utilisateur avec checkboxes
 * 
 * @param timings Créneaux horaires du site
 * @param includingOccurrences Occurrences actuellement incluses dans le filtre
 * @param onChange Fonction appelée lors du changement de sélection
 * @returns Composant React de filtre multiple par créneaux
 */
export const SpotMultiTimingsFilter = ({
  timings,
  includingOccurrences,
  onChange,
}: MultiProps) => {
  const toggleOccurrence = (
    every: CRM.SpotTimingsOccurence,
    include: boolean,
  ) => {
    const alreadyIncludingIndex = includingOccurrences.findIndex(
      occurrence =>
        occurrence.hour === every.hour && occurrence.minute === every.minute,
    );
    if (alreadyIncludingIndex !== -1 && !include) {
      onChange(Arrays.remove(includingOccurrences, alreadyIncludingIndex));
    } else if (alreadyIncludingIndex === -1 && include) {
      onChange(includingOccurrences.concat(every));
    }
  };

  return (
    <Box>
      <Typography>
        <b>Créneaux</b>
      </Typography>
      <Box>
        {timings.every
          .map(every => ({ ...every, label: labelOf(every) }))
          .map(every => (
            <FormControlLabel
              key={every.label}
              label={every.label}
              labelPlacement="end"
              control={
                <Checkbox
                  color="secondary"
                  onChange={event => {
                    toggleOccurrence(every, event.target.checked);
                  }}
                  checked={includingOccurrences.some(
                    occurrence =>
                      occurrence.hour === every.hour &&
                      occurrence.minute === every.minute,
                  )}
                />
              }
            />
          ))}
      </Box>
    </Box>
  );
};
